<template>
  <v-breadcrumbs v-if="crumbs.length" :items="crumbs" divider="/" class="app-breadcrumbs pa-0">
    <template v-slot:item="{ item }">
      <router-link v-if="item.type === 'link'" :to="item.to" class="crumb crumb--link">
        {{ item.raw ? item.text : $t(item.text) }}
      </router-link>
      <span v-else class="crumb" :class="{ 'crumb--current': item.type === 'current' }">
        {{ item.raw ? item.text : $t(item.text) }}
      </span>
    </template>
  </v-breadcrumbs>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { buildNavMenus, findMenuChain, getNodeTarget } from '@/module/navMenu'

export default {
  name: 'AppBreadcrumbs',
  computed: {
    crumbs() {
      const path = this.$route.path
      // 首页不展示面包屑；其他页也不再带 Home，缩短路径层级
      if (path === '/') {
        return []
      }

      const tree = buildNavMenus({ $p: this.$p, clientId: this.clientId })
      const items = []

      // 1) 先按当前完整路径精确匹配侧边栏叶子（列表页 / 顶级页）
      let chain = findMenuChain(tree, path)
      let detailName = null
      let detailRoot = null
      let detailRaw = true
      // 仅应用类多级详情才拼 tab；/ticket/:tid 等二级详情不走 detailTab
      let useDetailTab = false

      // 2) 详情页 / 下单浏览页：侧边栏无对应叶子，用路径首段回退匹配所属列表页
      if (!chain) {
        const segs = path.split('/').filter(Boolean)
        if (segs.length) {
          const listPath = `/${segs[0]}`
          chain = findMenuChain(tree, listPath)
          // 下单浏览：/{service}/ordering，末级用路由 meta.title（Ordering），勿当成实例详情
          if (chain && segs.length === 2 && segs[1] === 'ordering') {
            const meta = this.$route.meta || {}
            detailName = meta.title || 'menu.ordering[0]'
            detailRaw = false
            detailRoot = listPath
          } else if (chain && segs.length >= 3 && this.appInfo && this.appInfo.name) {
            // 应用类详情：/applications/:id/...，依赖已加载的 appInfo
            detailName = this.appInfo.name
            detailRoot = `/${segs[0]}/${segs[1]}`
            detailRaw = true
            useDetailTab = true
          } else if (chain && segs.length >= 2 && path !== listPath) {
            // 列表下二级详情（如 /ticket/:tid）：列表可点，末级用路由 meta.title
            const meta = this.$route.meta || {}
            if (meta.title) {
              detailName = meta.title
              detailRaw = false
            } else if (this.$route.name) {
              detailName = this.$route.name
              detailRaw = true
            } else {
              detailName = segs[segs.length - 1]
              detailRaw = true
            }
            detailRoot = listPath
          }
        }
      }

      if (chain) {
        chain.forEach((node, idx) => {
          const isLeaf = idx === chain.length - 1
          if (node.menus) {
            // 分组：仅展示，不可点
            items.push({ text: node.text, type: 'group' })
          } else if (isLeaf && !detailName) {
            // 链尾叶子且非详情页：即当前页
            items.push({ text: node.text, type: 'current' })
          } else {
            // 详情页场景下，作为可点击的祖先列表页
            items.push({ text: node.text, to: getNodeTarget(node), type: 'link' })
          }
        })
        if (detailName) {
          const tab = useDetailTab ? this.detailTab : null
          if (tab) {
            // 实例名作为可点链接（回到详情根/概览），当前 tab 作为末级
            items.push({ text: detailName, to: detailRoot, type: 'link', raw: detailRaw })
            items.push({ text: tab.text, type: 'current', raw: tab.raw })
          } else {
            items.push({ text: detailName, type: 'current', raw: detailRaw })
          }
        }
      } else {
        // 3) 兜底：不在侧边栏的独立页。优先用 matched 上各自的 meta.title 拼层级
        // （如 /account/verify-info → Account / Verification Information），避免只吃合并后的 meta
        const titled = this.$route.matched.filter((r) => r.meta && r.meta.title)
        if (titled.length) {
          titled.forEach((record, idx) => {
            const isLast = idx === titled.length - 1
            items.push({
              text: record.meta.title,
              type: isLast ? 'current' : 'group'
            })
          })
        } else {
          const meta = this.$route.meta || {}
          const text = meta.title || this.$route.name || ''
          if (text) {
            items.push({ text, type: 'current', raw: !meta.title })
          }
        }
      }

      return items
    },
    // 详情子页（tab）标签：优先复用详情左侧菜单的 i18n 文案，回退到路由 name
    detailTab() {
      const info = this.appInfo
      if (!info || !info.name || !info.product_type) return null
      let menus = []
      try {
        menus = this.getCategoryMenus(info)
      } catch (e) {
        // appInfo 结构不完整时静默降级，走路由 name 兜底
        menus = []
      }
      const hit = menus.find((m) => m.path === this.$route.path)
      if (hit) return { text: hit.text, raw: false }
      const matched = this.$route.matched
      const last = matched.length ? matched[matched.length - 1] : null
      if (last && last.name) return { text: last.name, raw: true }
      return null
    },
    ...mapState('oAuth', ['clientId']),
    ...mapState('application', ['appInfo']),
    ...mapGetters('application', ['getCategoryMenus'])
  }
}
</script>

<style scoped lang="scss">
.app-breadcrumbs {
  // 强制单行：避免面包屑换行撑高固定高度的 AppBar，进而遮挡下方内容
  flex-wrap: nowrap;
  overflow: hidden;

  font-size: 14px;

  ::v-deep .v-breadcrumbs__item {
    font-size: 14px;
  }

  ::v-deep .v-breadcrumbs__divider {
    padding: 0 8px;
    color: #bbb;
  }

  ::v-deep li {
    white-space: nowrap;
    font-size: 14px;
  }

  .crumb {
    color: #888;
    white-space: nowrap;

    &--link {
      text-decoration: none;
      cursor: pointer;

      &:hover {
        color: var(--v-primary-base);
      }
    }

    &--current {
      color: #333;
      font-weight: 600;
      // 实例名 / 当前页过长时省略，保证单行
      max-width: 260px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      vertical-align: bottom;
    }
  }
}
</style>
